import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { images } from '../utils/images';
import { 
  CheckCircle2, 
  Users, 
  GraduationCap,
  Phone,
  Briefcase,
  Award,
  MapPin,
  TrendingUp,
  FileText,
  Clock,
  Bus,
  Handshake,
  Target,
  Heart,
  BookOpen,
  UserCheck
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const SlesPage = () => {
  const faqs = [
    {
      question: "What is School Leaver Employment Supports (SLES)?",
      answer: "School Leaver Employment Supports (SLES) is an NDIS funded program to help young people with disability transition from school to work or further education. Known as School Leavers Employment Services, it's delivered by NDIS SLES Providers."
    },
    {
      question: "Who qualifies for SLES through the NDIS?",
      answer: "To access NDIS-Funded School Leaver Employment Supports, you must have an approved NDIS plan, be aged between 15 and 25 (or still enrolled at school), and require assistance to prepare for employment. Discuss including SLES Logan or other SLES Providers Near Me with your Local Area Coordinator or NDIA planner."
    },
    {
      question: "What types of skills are developed in a SLES program?",
      answer: "The SLES Program focuses on practical employability skills—resume writing, interview techniques, workplace communication, and digital literacy—plus daily living skills like time management, money handling, and travel training. NDIS SLES Providers Brisbane often arrange real-world experiences across industries."
    },
    {
      question: "When should I start planning for SLES during high school?",
      answer: "Ideally, begin exploring SLES Logan or SLES Brisbane Bayside options in Year 10 or Year 11. Early engagement with your school's career counsellor and local NDIS SLES Providers ensures tailored training pathways and work experience placements are secured before you graduate."
    },
    {
      question: "How long can a young person receive SLES support?",
      answer: "Participants can access NDIS-Funded School Leaver Employment Supports for up to three years after leaving school, or until they turn 25—whichever comes first. Duration is based on your goals and reasonable and necessary supports in your NDIS plan, with progress reviewed regularly."
    },
    {
      question: "Can SLES help me find a job or just prepare me for one?",
      answer: "While the SLES Program primarily focuses on preparation through skills training, work trials, and industry exposure, many NDIS SLES Providers Brisbane also offer job placement assistance. Their employer networks help participants secure paid or volunteer roles."
    },
    {
      question: "What is the difference between SLES and DES (Disability Employment Services)?",
      answer: "School Leaver Employment Supports (SLES) targets school leavers with disability to build foundational work skills and confidence before entering the job market. DES (Disability Employment Services) supports ongoing job search, placement, and retention post-school or post-training."
    },
    {
      question: "Is SLES available for students who finished school a year or two ago?",
      answer: "Yes. If you left school within the past three years and are under 25, you can still access SLES through your NDIS plan. Whether you're with SLES Providers Near Me in Logan, Brisbane, or elsewhere, providers can customize a program to meet your current needs."
    },
    {
      question: "Do SLES participants get work experience placements?",
      answer: "Absolutely. Work experience is a core component of the SLES Program, offering on-the-job exposure in industries aligned with each participant's goals. Placements range from short trials to longer internships, typically arranged by SLES Providers Brisbane in partnership with local employers."
    },
    {
      question: "How do I include SLES in my NDIS plan?",
      answer: "To add NDIS-Funded School Leaver Employment Supports to your plan, discuss your goals with your Local Area Coordinator or NDIA planner during your review meeting. Request supports under the Capacity Building budget, specifying services like vocational training, work experience, or job readiness coaching with NDIS SLES Providers."
    }
  ];

  const keySkills = [
    {
      icon: FileText,
      title: "Resume writing and job interview preparation",
      description: "Participants craft professional resumes and practise interview techniques through mock interviews with local employer partners. This component is central to our School Leavers Employment Services philosophy, bridging theory and practice."
    },
    {
      icon: Users,
      title: "Communication, teamwork, and time management",
      description: "Group projects build collaboration and project-management skills, while time-management workshops teach prioritisation and organisation."
    },
    {
      icon: Bus,
      title: "Using public transport and budgeting for independence",
      description: "Practical modules include planning bus and train routes across Brisbane and managing personal budgets—skills that foster independence in both work and community life."
    }
  ];

  const careerExploration = [
    "Retail and customer service",
    "Information technology and digital media",
    "Hospitality and tourism"
  ];

  const scrollToContact = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="School Leaver Employment Supports (SLES)" description="SLES in Brisbane helps young people with disabilities build skills, confidence and explore career pathways after school. Trusted NDIS SLES provider." />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ color: '#587740' }}
                data-testid="sles-hero-title"
              >
                School Leaver Employment Supports (SLES)
              </h1>
              <p 
                className="text-lg md:text-xl leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Leaving school is one of the biggest transitions in a young person's life — and it can feel scary, especially if you're not sure what comes next. We're here to make sure no young person with a disability gets left behind.
              </p>
              <p 
                className="text-base leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                In simple terms — SLES helps school leavers build the skills they need for work and life after school. We provide hands-on training, real work experience, and personal mentoring — all tailored to their strengths, interests, and goals. Not a generic program — a plan built around who they are.
              </p>
              <p 
                className="text-base leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                Many providers treat SLES as a box-ticking exercise. We treat it as a young person's future. As a leading NDIS Provider, we work closely with NDIA Planners to make sure every participant's plan reflects their true potential.
              </p>
              <p 
                className="text-base leading-relaxed italic mb-4"
                style={{ color: '#6c935e' }}
              >
                We see the psychological struggles that go unseen. You are heard and valued and in control.
              </p>
              <p 
                className="text-sm font-semibold mb-8"
                style={{ color: '#587740' }}
              >
                Free consultation for parents and carers — let's talk about your young person's future, no cost and no pressure.
              </p>
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
                data-testid="sles-hero-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={images.sles4}
                alt="SLES - Career pathways training"
                className="w-full h-[400px] md:h-[500px] object-cover"
                data-testid="sles-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is SLES Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
                data-testid="sles-what-is-title"
              >
                What is School Leaver Employment Supports (SLES)?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  The SLES Program is a specialised initiative under the NDIS (National Disability Insurance Scheme) designed to support School Leavers with Disability as they leave school and embark on Post School Pathways. Through structured learning activities and workplace experiences, the program fosters independence and builds employability skills.
                </p>
                <p 
                  className="text-lg leading-relaxed mb-4"
                  style={{ color: '#5e5e5e' }}
                >
                  As one of the foremost NDIS SLES Providers, we combine classroom-based learning, community engagement, and on the job experiences to create a holistic pathway from school to work—facilitating the Transition from School to Work for young people.
                </p>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: '#6c935e' }}
                >
                  We also provide NDIS Consulting to help families navigate plan reviews and include the right supports for your goals.
                </p>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={images.sles5}
                  alt="SLES participants in training"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Overview of the SLES Program Under the NDIS
              </h2>
              <p 
                className="text-lg md:text-xl max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Under the NDIS, NDIS-Funded School Leaver Employment Supports provide funding for up to two years of assistance. NDIS Participants receive:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {[
                { icon: Target, text: "Capacity Building Supports tailored to individual goals" },
                { icon: UserCheck, text: "Career Coaching for School Leavers, including one-on-one mentoring" },
                { icon: Award, text: "Access to industry-relevant certifications and training" },
                { icon: Briefcase, text: "Guidance on NDIS Finding and Keeping a Job" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="border-none shadow-lg">
                    <CardContent className="p-6 flex items-start space-x-4">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#AABC5A' }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-lg leading-relaxed" style={{ color: '#5e5e5e' }}>
                        {item.text}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* How SLES supports */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 
                className="text-2xl font-bold mb-4"
                style={{ color: '#587740' }}
              >
                How SLES Supports School Leavers with Disabilities
              </h3>
              <p 
                className="text-lg leading-relaxed mb-4"
                style={{ color: '#5e5e5e' }}
              >
                The SLES Program emphasises practical, NDIS Employment Support, rotating participants through simulated work environments—such as retail, hospitality, and digital media—to gain on-the-job skills. They also benefit from:
              </p>
              <ul className="space-y-3">
                {[
                  "One-on-one coaching to set career goals",
                  "Group workshops on workplace rights and responsibilities",
                  "Regular feedback sessions to track progress"
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                    <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Who is Eligible for SLES?
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Eligibility for School Leaver Employment Supports (SLES) is determined by age, disability, and NDIS plan requirements. Our team guides families through each step, ensuring everyone can access the support they need as an NDIS Participant.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 
                  className="text-2xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  Age and NDIS Plan Requirements
                </h3>
                <p className="text-base mb-4" style={{ color: '#5e5e5e' }}>
                  To access the School Leaver Employment Supports (SLES), participants must meet specific criteria:
                </p>
                <ul className="space-y-3">
                  {[
                    "Age: Aged 15–21 at the time of enrolment",
                    "NDIS Plan: Must hold an active NDIS plan",
                    "Desire to Work: Demonstrate interest in paid work or voluntary placements"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 
                  className="text-2xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  How to Get SLES Funding Included in Your Plan
                </h3>
                <p className="text-base mb-4" style={{ color: '#5e5e5e' }}>
                  School Leavers and their families work with our planners to:
                </p>
                <ul className="space-y-3">
                  {[
                    "Gather evidence of past support needs",
                    "Draft goals centred on post-school employment",
                    "Submit plan review requests to the NDIA"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base mt-4 font-semibold" style={{ color: '#587740' }}>
                  Our expert team liaises directly with NDIA Planners to secure funding for the SLES Program.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our SLES Program Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Our SLES Program in Brisbane: What You Can Expect
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Life Care and Wellness offers comprehensive School Leavers Employment Services designed to guide and support every step of the journey. Our Brisbane-based service offers flexible delivery options across Greater Brisbane, including hubs in SLES Logan, SLES Brisbane Bayside, and central city locations for those searching for SLES Providers Near Me.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.sles1}
                  alt="SLES program activities"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Individualised Support Tailored to Your Goals
                </h3>
                <p className="text-lg leading-relaxed mb-6" style={{ color: '#5e5e5e' }}>
                  Every participant receives a bespoke plan, integrating their interests—whether creative arts, technology, or hospitality—with industry connections for paid work or volunteer placements.
                </p>

                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Real-world Skills and Workplace Readiness Training
                </h3>
                <p className="text-base mb-3" style={{ color: '#5e5e5e' }}>
                  Hands-on workshops cover:
                </p>
                <ul className="space-y-2">
                  {[
                    "Resume writing and job interview preparation through mock interviews",
                    "Health and safety requirements in line with NDIS Operational Guidelines",
                    "Workplace communication, teamwork, and time management"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Skills Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Key Skills We Help You Develop
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Our curriculum focuses on essential employment skills, ensuring participants leave the SLES Program ready for success.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {keySkills.map((skill, index) => {
                const Icon = skill.icon;
                return (
                  <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-8">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                        style={{ backgroundColor: '#AABC5A' }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                        {skill.title}
                      </h3>
                      <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                        {skill.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
                data-testid="sles-skills-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Career Pathways Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Career Pathways and Exploration
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Our program is designed to spark curiosity and discover each young person's unique strengths.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  How We Help You Discover Your Strengths and Interests
                </h3>
                <p className="text-base mb-4" style={{ color: '#5e5e5e' }}>
                  Through career assessments, job shadowing, and employer visits, participants explore fields such as:
                </p>
                <ul className="space-y-3 mb-8">
                  {careerExploration.map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Accessing Traineeships, Apprenticeships, and Work Experience
                </h3>
                <p className="text-base" style={{ color: '#5e5e5e' }}>
                  As SLES Providers Brisbane, we maintain partnerships with local businesses to facilitate paid traineeships, apprenticeships, and structured work placement, ensuring smooth Transition from School to Work.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.sles2}
                  alt="Career pathways exploration"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support for Families Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Support for Families and Carers
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                We recognise that families and carers are essential to the SLES journey, and we prioritise clear communication and collaborative goal setting.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{ backgroundColor: '#81b956' }}
                  >
                    <Heart className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                    Helping Families Understand the SLES Journey
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                    Informational workshops and family meetings ensure everyone understands program milestones and terminology—especially around NDIS SLES Providers, NDIS Employment Support, and funding pathways. Additionally, we coordinate with NDIS Support Coordination services to seamlessly integrate all parts of your plan.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div 
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                    style={{ backgroundColor: '#81b956' }}
                  >
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                    Regular Updates and Collaborative Goal Tracking
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                    Monthly progress reports and joint review sessions keep families in the loop, fostering a supportive environment for participant growth.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Why Choose Our Brisbane-Based SLES Services?
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                As one of the leading SLES providers in Brisbane, Life Care and Wellness offers personalised pathways to employment for young Australians. With deep roots in the community, our service stands out among NDIS Providers.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Local Employer Partnerships and Community Involvement
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#5e5e5e' }}>
                  We collaborate with over 50 Brisbane businesses, offering participants exclusive internships and job placements. Our engagement goes beyond training: we host community events to showcase student work and build employer networks.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.sles3}
                  alt="Community involvement and partnerships"
                  className="w-full h-[350px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Our Step-by-Step SLES Process
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                Our transparent process ensures participants and families know exactly what to expect at every stage.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4 text-white text-xl font-bold"
                    style={{ backgroundColor: '#6c9c48' }}
                  >
                    1
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                    Initial Consultation and Goal Planning
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                    We begin with a comprehensive intake assessment to identify strengths, interests, and support needs. Goals are co-created with participants and families, guided by NDIS Operational Guidelines.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4 text-white text-xl font-bold"
                    style={{ backgroundColor: '#6c9c48' }}
                  >
                    2
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                    Ongoing Mentoring and Progress Evaluation
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                    Throughout the program, participants work with dedicated mentors who provide regular feedback, adjust training plans, and prepare individuals for the shift towards open employment.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg">
                <CardContent className="p-8">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4 text-white text-xl font-bold"
                    style={{ backgroundColor: '#6c9c48' }}
                  >
                    3
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#587740' }}>
                    Local Employer Partnerships
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#5e5e5e' }}>
                    We collaborate with over 50 Brisbane businesses, offering participants exclusive internships and job placements. Our engagement goes beyond training: we host community events to showcase student work and build employer networks.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Transitioning Section */}
      <section className="py-20" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Transitioning from SLES to Open Employment
              </h2>
              <p 
                className="text-lg max-w-4xl mx-auto"
                style={{ color: '#5e5e5e' }}
              >
                A key outcome of our service is sustainable employment. To achieve this, we partner closely with Disability Employment Services (DES).
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-md">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Linking with Disability Employment Services (DES)
                </h3>
                <p className="text-lg leading-relaxed" style={{ color: '#5e5e5e' }}>
                  As participants near program completion, we facilitate introductions to DES providers, ensuring a seamless Transition from School to Work. Our ties with School Leavers Employment Services specialists help maintain momentum as participants move into the workforce.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-md">
                <h3 className="text-2xl font-bold mb-4" style={{ color: '#587740' }}>
                  Building Long-term Employment Sustainability
                </h3>
                <p className="text-base mb-4" style={{ color: '#5e5e5e' }}>
                  Post-placement support includes:
                </p>
                <ul className="space-y-3">
                  {[
                    "Check-in calls to both employer and employee",
                    "On-the-job coaching for the first 12 weeks",
                    "Access to refresher workshops on workplace skills",
                    "Guidance on NDIS Finding and Keeping a Job"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: '#81b956' }} />
                      <span className="text-base" style={{ color: '#5e5e5e' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div 
              className="mt-12 p-8 rounded-2xl text-center bg-white shadow-md"
            >
              <p 
                className="text-lg leading-relaxed"
                style={{ color: '#5e5e5e' }}
              >
                Our commitment to excellence as one of the leading NDIS SLES Providers in Brisbane ensures that School Leaver Employment Supports (SLES) graduates emerge with confidence, practical skills, and clear Post-School Pathways to meaningful work. Whether you're exploring School Leavers Employment Services, seeking support in SLES Logan, or comparing SLES Providers Brisbane, our dedicated team is here to guide every step of the journey. By choosing our SLES Program, you're investing in a future where ability is celebrated, independence is achievable, and employment potential is limitless.
              </p>
            </div>

            <div className="text-center mt-8">
              <a href="tel:+611300404229">
                <Button
                  size="lg"
                  className="text-white hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#81b956' }}
                  data-testid="sles-call-now-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-testid="sles-faq-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                style={{ color: '#587740' }}
              >
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border rounded-lg px-6 shadow-sm"
                  data-testid={`sles-faq-item-${index}`}
                >
                  <AccordionTrigger 
                    className="text-left hover:no-underline py-6"
                    style={{ color: '#587740' }}
                  >
                    <span className="text-lg font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-base leading-relaxed pb-6"
                    style={{ color: '#5e5e5e' }}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#81b956' }}
                data-testid="sles-faq-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <VideoBackground variant="dark" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Every Young Person Deserves a Bright Future
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Let our experienced team help your young person transition from school to meaningful employment with confidence and support. Free consultation, free action plan — because their future shouldn't wait.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="text-base px-8 py-6"
                style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
                data-testid="sles-cta-contact-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Get Your Free Consultation
              </Button>
              <a href="/get-on-ndis-free">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base px-8 py-6 border-2 border-white text-white hover:bg-white/10"
                  data-testid="sles-cta-home-btn"
                >
                  Get on the NDIS Free
                </Button>
              </a>
            </div>
          </div>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default SlesPage;
